import { Link } from "react-router-dom";
import { Bell, BellRing, Check, Inbox } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  useNotifications,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
} from "@/features/notifications/hooks";
import { cn } from "@/lib/utils";

function timeAgo(iso: string) {
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return "";
  const diff = Date.now() - t;
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(t).toLocaleDateString();
}

export default function NotificationBell() {
  const q = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const items = q.data ?? [];
  const unread = items.filter((n) => !n.is_read).length;
  const preview = items.slice(0, 6);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={
            unread > 0 ? `Notifications, ${unread} unread` : "Notifications"
          }
        >
          {unread > 0 ? (
            <BellRing className="size-5" />
          ) : (
            <Bell className="size-5" />
          )}
          {unread > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold tabular-nums text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[360px] p-0">
        <div className="flex items-center justify-between px-3 py-2.5">
          <div className="text-sm font-semibold">Notifications</div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs"
              onClick={(e) => {
                e.preventDefault();
                markAllRead.mutate();
              }}
              disabled={markAllRead.isPending}
            >
              <Check className="size-3.5" />
              Mark all read
            </Button>
          )}
        </div>
        <div className="border-t" />

        {q.isLoading && (
          <div className="px-3 py-8 text-center text-xs text-muted-foreground">
            Loading…
          </div>
        )}

        {!q.isLoading && preview.length === 0 && (
          <div className="flex flex-col items-center gap-1 px-3 py-8 text-center">
            <Inbox className="size-6 text-muted-foreground" />
            <p className="text-sm font-medium">You&apos;re all caught up</p>
            <p className="text-xs text-muted-foreground">
              New notifications will appear here.
            </p>
          </div>
        )}

        {!q.isLoading && preview.length > 0 && (
          <ul className="max-h-[360px] divide-y overflow-y-auto">
            {preview.map((n) => (
              <li
                key={n.id}
                className={cn(
                  "flex cursor-pointer items-start gap-2.5 px-3 py-2.5 transition-colors hover:bg-accent",
                  !n.is_read && "bg-accent/40",
                )}
                onClick={() => {
                  if (!n.is_read) markRead.mutate(n.id);
                }}
              >
                <span
                  className={cn(
                    "mt-1.5 size-2 shrink-0 rounded-full",
                    n.is_read ? "bg-transparent" : "bg-indigo-500",
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p
                    className={cn(
                      "truncate text-sm",
                      n.is_read ? "font-normal" : "font-semibold",
                    )}
                  >
                    {n.title}
                  </p>
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {n.message}
                  </p>
                  <p className="mt-0.5 text-[11px] text-muted-foreground/80">
                    {timeAgo(n.created_at)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="border-t" />
        <Link
          to="/notifications"
          className="block px-3 py-2 text-center text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground"
        >
          View all notifications
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
